import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import TableShapeGlyph from './TableShapeGlyph';
import {
  DialogHeader,
  FieldLabel,
  FP,
  fpDialogActionsSx,
  fpDialogPaperProps,
  pillSx,
} from './lib/ui';

const SHAPE_OPTIONS = [
  { id: 'square', label: 'Square' },
  { id: 'round', label: 'Round' },
  { id: 'rectangle', label: 'Rect' },
  { id: 'booth', label: 'Booth' },
  { id: 'bar', label: 'Bar' },
];

const SEAT_PRESETS = [2, 4, 6, 8];
const COUNT_PRESETS = [1, 2, 4, 6, 8, 10];

const customNumberSx = {
  width: 68,
  '& .MuiOutlinedInput-root': {
    height: 38,
    borderRadius: '10px',
  },
  '& input': {
    textAlign: 'center',
    fontWeight: 700,
    fontSize: 14,
    py: 0,
  },
};

const AddTableModal = ({
  open,
  onClose,
  presets = [],
  layoutTemplates = [],
  onAdd,
  onAddPack,
}) => {
  const [shape, setShape] = useState('square');
  const [seats, setSeats] = useState(4);
  const [count, setCount] = useState(1);
  const [busy, setBusy] = useState(false);

  const matched = useMemo(() => {
    const exact = presets.find(
      (p) => p.shape === shape && p.capacity === seats
    );
    if (exact) {
      return exact;
    }
    const byShape = presets.filter((p) => p.shape === shape);
    const nearest =
      byShape.find((p) => p.capacity >= seats) ||
      byShape[byShape.length - 1] ||
      presets[0];
    return nearest
      ? { ...nearest, capacity: seats, shape }
      : {
          shape,
          label: shape,
          capacity: seats,
          width: 120,
          height: 120,
        };
  }, [presets, shape, seats]);

  const handleAdd = async () => {
    setBusy(true);
    try {
      await onAdd(matched, count);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={fpDialogPaperProps}
    >
      <DialogHeader
        title="Add tables"
        subtitle="Pick a shape and size, or drop in a ready-made pack."
        onClose={onClose}
      />
      <DialogContent sx={{ px: 3 }}>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <Box>
            <FieldLabel>Shape</FieldLabel>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 1 }}
            >
              {SHAPE_OPTIONS.map((opt) => {
                const active = shape === opt.id;
                return (
                  <Box
                    key={opt.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setShape(opt.id)}
                    onKeyDown={(e) => e.key === 'Enter' && setShape(opt.id)}
                    sx={{
                      width: 84,
                      py: 1.25,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.75,
                      cursor: 'pointer',
                      borderRadius: 2.5,
                      border: active
                        ? `2px solid ${FP.primary}`
                        : `1px solid ${FP.line}`,
                      bgcolor: active ? FP.primarySoft : '#fff',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: active ? FP.primary : FP.faint,
                        bgcolor: active ? FP.primarySoft : FP.surface,
                      },
                    }}
                  >
                    <TableShapeGlyph
                      shape={opt.id}
                      width={opt.id === 'rectangle' || opt.id === 'bar' ? 40 : 28}
                      height={opt.id === 'bar' ? 18 : 28}
                    />
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: active ? FP.primaryDark : FP.sub,
                      }}
                    >
                      {opt.label}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2.5}
            alignItems="flex-start"
          >
            <Box sx={{ flex: 1 }}>
              <FieldLabel>Seats</FieldLabel>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ mt: 1, flexWrap: 'wrap' }}
                alignItems="center"
              >
                {SEAT_PRESETS.map((n) => (
                  <Button
                    key={n}
                    size="small"
                    variant="outlined"
                    onClick={() => setSeats(n)}
                    sx={pillSx(seats === n)}
                  >
                    {n}
                  </Button>
                ))}
                <TextField
                  size="small"
                  type="number"
                  value={seats}
                  onChange={(e) =>
                    setSeats(
                      Math.min(99, Math.max(1, Number(e.target.value) || 1))
                    )
                  }
                  sx={customNumberSx}
                  inputProps={{ 'aria-label': 'Custom seats', min: 1, max: 99 }}
                />
              </Stack>
            </Box>
            <Box sx={{ flex: 1 }}>
              <FieldLabel>Count</FieldLabel>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ mt: 1, flexWrap: 'wrap' }}
                alignItems="center"
              >
                {COUNT_PRESETS.map((n) => (
                  <Button
                    key={n}
                    size="small"
                    variant="outlined"
                    onClick={() => setCount(n)}
                    sx={pillSx(count === n)}
                  >
                    {n}
                  </Button>
                ))}
                <TextField
                  size="small"
                  type="number"
                  value={count}
                  onChange={(e) =>
                    setCount(
                      Math.min(40, Math.max(1, Number(e.target.value) || 1))
                    )
                  }
                  sx={customNumberSx}
                  inputProps={{ 'aria-label': 'Custom count', min: 1, max: 40 }}
                />
              </Stack>
            </Box>
          </Stack>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderRadius: 3,
              bgcolor: FP.surface,
              border: `1px solid ${FP.lineSoft}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 96,
                height: 96,
                borderRadius: 2,
                bgcolor: '#fff',
                border: `1px solid ${FP.lineSoft}`,
                flexShrink: 0,
              }}
            >
              <TableShapeGlyph
                shape={matched.shape}
                width={Math.min(72, matched.width / 2)}
                height={Math.min(72, matched.height / 2)}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, color: FP.ink }}>
                {matched.label || matched.shape} · {matched.capacity} seats
              </Typography>
              <Typography variant="body2" sx={{ color: FP.sub, mt: 0.25 }}>
                {matched.width}×{matched.height} px
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  mt: 1,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1.5,
                  bgcolor: FP.primarySoft,
                  color: FP.primaryDark,
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                Adds {count} table{count === 1 ? '' : 's'}
              </Box>
            </Box>
          </Box>

          {layoutTemplates?.length ? (
            <Box>
              <FieldLabel>Quick packs</FieldLabel>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ mt: 1, flexWrap: 'wrap' }}
              >
                {layoutTemplates.map((tpl) => (
                  <Box
                    key={tpl.id}
                    role="button"
                    tabIndex={0}
                    onClick={async () => {
                      if (busy || !onAddPack) return;
                      setBusy(true);
                      try {
                        await onAddPack(tpl.id);
                        onClose();
                      } finally {
                        setBusy(false);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && onAddPack && !busy) {
                        e.currentTarget.click();
                      }
                    }}
                    sx={{
                      px: 1.5,
                      py: 1,
                      minWidth: 120,
                      cursor: busy ? 'default' : 'pointer',
                      opacity: busy ? 0.6 : 1,
                      borderRadius: 2.5,
                      border: `1px solid ${FP.line}`,
                      bgcolor: '#fff',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: FP.primary,
                        bgcolor: FP.primarySoft,
                        boxShadow: FP.shadowSm,
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, fontSize: 13, color: FP.ink }}>
                      {tpl.label}
                    </Typography>
                    {tpl.tip ? (
                      <Typography sx={{ fontSize: 11, color: FP.sub, mt: 0.25 }}>
                        {tpl.tip}
                      </Typography>
                    ) : null}
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={fpDialogActionsSx}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={busy}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            px: 2.5,
            bgcolor: FP.primary,
            boxShadow: '0 6px 16px rgba(45,127,249,0.30)',
            '&:hover': { bgcolor: FP.primaryDark },
          }}
        >
          {busy ? 'Adding…' : `Add ${count} table${count === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTableModal;
